import React, { useState, useEffect } from 'react';
import { ArrowLeft, FileText, Copy, Calendar, CreditCard, Clock, ChevronDown, ChevronRight } from 'lucide-react';
import { maintenanceService } from '../../services/maintenanceService';
import type { MaintenanceContract, MaintenanceReport } from '../../types/maintenance';
import HoursProgressBar from './HoursProgressBar';
import TasksList from './TasksList';
import MaintenanceChecklist from './MaintenanceChecklist';
import MonthlyReportView from './MonthlyReportView';

interface ContractDetailProps {
  contractId: string;
  onBack: () => void;
}

const ContractDetail: React.FC<ContractDetailProps> = ({ contractId, onBack }) => {
  const [contract, setContract] = useState<MaintenanceContract | null>(null);
  const [hours, setHours] = useState({ total: 0, support: 0, improvement: 0, remaining: 0, extra: 0 });
  const [reports, setReports] = useState<MaintenanceReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [showHistory, setShowHistory] = useState(false);
  const [reportMonth, setReportMonth] = useState<string | null>(null);
  const [checklistMonth, setChecklistMonth] = useState(new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Madrid', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date()).slice(0, 7));
  const [copied, setCopied] = useState(false);

  const currentMonth = new Date().toISOString().slice(0, 7);

  useEffect(() => {
    loadData();
  }, [contractId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [c, h, r] = await Promise.all([
        maintenanceService.getContractById(contractId),
        maintenanceService.getMonthlyHours(contractId, currentMonth),
        maintenanceService.getReportsByContract(contractId),
      ]);
      setContract(c);
      setHours(h);
      setReports(r);
    } catch (err) {
      console.error('Error loading contract detail:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyPortalLink = () => {
    if (!contract) return;
    const url = `${window.location.origin}/portal/mantenimiento/${contract.portal_token}`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleGenerateReport = async () => {
    if (!contract) return;
    await maintenanceService.getOrCreateMonthlyReport(contract.id, currentMonth);
    setReportMonth(currentMonth);
  };

  if (loading) {
    return <div className="text-center py-12 text-gray-500">Cargando contrato...</div>;
  }

  if (!contract) {
    return <div className="text-center py-12 text-red-500">Contrato no encontrado</div>;
  }

  // If viewing a report, show report view
  if (reportMonth) {
    return (
      <MonthlyReportView
        contract={contract}
        reportMonth={reportMonth}
        onBack={() => { setReportMonth(null); loadData(); }}
      />
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <button onClick={onBack} className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
          <ArrowLeft className="w-4 h-4" />
          Volver a contratos
        </button>
        <div className="flex items-center gap-2">
          <button
            onClick={handleCopyPortalLink}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
          >
            <Copy className="w-4 h-4" />
            {copied ? 'Copiado!' : 'Copiar enlace portal'}
          </button>
          <button
            onClick={handleGenerateReport}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <FileText className="w-4 h-4" />
            Generar informe del mes
          </button>
        </div>
      </div>

      <div className="mb-6 space-y-3">
        <label className="block text-sm">Periodo de la checklist<input type="month" required value={checklistMonth} onChange={e => setChecklistMonth(e.target.value)} className="block border rounded-lg p-2" /></label>
        <MaintenanceChecklist key={`${contract.id}:${checklistMonth}`} contractId={contract.id} month={checklistMonth} startDate={contract.start_date} canManage />
      </div>
      {/* Contract info */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-1">{contract.client_name}</h2>
        <p className="text-gray-500 mb-4">{contract.plan_name}</p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="flex items-center gap-2">
            <CreditCard className="w-4 h-4 text-gray-400" />
            <div>
              <p className="text-xs text-gray-500">Precio mensual</p>
              <p className="font-semibold">{contract.monthly_price}€</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-gray-400" />
            <div>
              <p className="text-xs text-gray-500">Horas mensuales</p>
              <p className="font-semibold">{contract.monthly_hours}h ({contract.support_hours}h soporte + {contract.improvement_hours}h mejoras)</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-gray-400" />
            <div>
              <p className="text-xs text-gray-500">Inicio</p>
              <p className="font-semibold">{new Date(contract.start_date).toLocaleDateString('es-ES')}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <CreditCard className="w-4 h-4 text-gray-400" />
            <div>
              <p className="text-xs text-gray-500">Forma de pago</p>
              <p className="font-semibold">{contract.payment_method} (dia {contract.billing_day})</p>
            </div>
          </div>
        </div>

        {/* Monthly hours summary */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Horas del mes actual</h3>
          <HoursProgressBar
            used={hours.total}
            included={contract.monthly_hours}
            extra_price={contract.extra_hour_price}
          />
          <div className="flex gap-6 mt-3 text-sm">
            <span className="text-gray-600">Soporte: <strong>{hours.support.toFixed(2)}h</strong> / {contract.support_hours}h</span>
            <span className="text-gray-600">Mejoras: <strong>{hours.improvement.toFixed(2)}h</strong> / {contract.improvement_hours}h</span>
            {hours.extra > 0 && (
              <span className="text-red-600 font-medium">Extra: {hours.extra.toFixed(2)}h ({(hours.extra * contract.extra_hour_price).toFixed(0)}€)</span>
            )}
          </div>
        </div>
      </div>

      {/* Tasks this month */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Tareas del mes actual</h3>
        {contract.project_id ? <p className="text-gray-600">Registra y valida las intervenciones desde la pestaña Mantenimiento del proyecto. Los informes usan esos mismos partes de horas.</p> : <TasksList contractId={contract.id} month={currentMonth} onTasksChange={loadData} />}
      </div>

      {/* Historical reports */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <button
          onClick={() => setShowHistory(!showHistory)}
          className="flex items-center gap-2 w-full text-left"
        >
          {showHistory ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          <h3 className="text-lg font-semibold text-gray-900">Historico de informes ({reports.length})</h3>
        </button>

        {showHistory && (
          <div className="mt-4 space-y-2">
            {reports.length === 0 ? (
              <p className="text-gray-500 text-sm">No hay informes generados</p>
            ) : (
              reports.map((r) => (
                <div
                  key={r.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer"
                  onClick={() => setReportMonth(r.report_month.slice(0, 7))}
                >
                  <div>
                    <p className="font-medium text-gray-900">
                      {new Date(r.report_month).toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}
                    </p>
                    <p className="text-sm text-gray-500">
                      {Number(r.total_hours_used).toFixed(2)}h usadas de {r.included_hours}h
                      {Number(r.extra_hours) > 0 && ` | +${Number(r.extra_hours).toFixed(2)}h extra`}
                    </p>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    r.status === 'sent' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {r.status === 'sent' ? 'Enviado' : 'Borrador'}
                  </span>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ContractDetail;
